import { PGlobalMenuItem, PGlobalMenuItemKeys, PGlobalMenuWithSubMenu } from '@previo/design-system'
import { computed } from 'vue'
import { Flag, useFeatureFlags } from '@previo/feature-flags'
import { $t } from '@previo/core'
import { Routes } from '~/router/routes'
import { usePropertyStore } from '~/store/property/property'
import { ModuleCheck, PrivilegeCheck } from '~/composables/usePermissionCheck'
import { Module } from '~/codebooks/acl/Module'
import { Privilege } from '~/codebooks/acl/Privilege'
import { usePropertyModuleStore } from '~/store/property/modules'
import { useCountryStrategy } from '~/layouts/components/l10n/useCountryStrategy'
import { CampaignCategory } from '~/pages/communication-template/codebooks/CampaignCategory'

const ff = useFeatureFlags()

export const modules = computed<(PGlobalMenuWithSubMenu | PGlobalMenuItem)[]>(() => {
  const modules: (PGlobalMenuWithSubMenu | PGlobalMenuItem)[] = []
  const propertyModuleStore = usePropertyModuleStore()
  const propertyStore = usePropertyStore()
  const countryStrategy = useCountryStrategy(propertyStore.getCountryId())

  if (!propertyModuleStore.isLoaded) {
    propertyModuleStore.load()
  }

  const hasReservationSettingsEnabled = computed(
    () => ff.isEnabled(Flag.PMS_SETTINGS_RESERVATION_ENABLED).value,
  )

  const isCommunicationModuleEnabled = computed(
    () => ff.isEnabled(Flag.PMS_COMMUNICATION_MODULE_ENABLED).value,
  )

  const isGiftVouchersEnabled = computed(
    () => ff.isEnabled(Flag.INVOICING_GIFT_VOUCHERS_ENABLED).value,
  )

  const isOffersEnabled = computed(() => ff.isEnabled(Flag.PMS_OFFERS_ENABLED).value)

  const isServiceMappingEnabled = computed(
    () => ff.isEnabled(Flag.PMS_SERVICE_MAPPING_ENABLED).value,
  )

  const isCompanyGridEnabled = computed(() => ff.isEnabled(Flag.PMS_MODULE_COMPANY_GRID).value)

  const isExchangeRatesEnabled = computed(() => ff.isEnabled(Flag.PMS_EXCHANGE_RATES).value)

  const isTaxSettingsEnabled = computed(() => ff.isEnabled(Flag.PMS_TAX_SETTINGS_ENABLED).value)

  const isConnectedRoomsEnabled = computed(
    () => ff.isEnabled(Flag.PMS_CONNECTED_ROOMS_ENABLED).value,
  )

  modules.push(
    new PGlobalMenuWithSubMenu(PGlobalMenuItemKeys.GUESTS_GROUP, $t('Guests'), 'user', [
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.OFFERS,
        $t('Reservation offers'),
        'envelope-open-text',
        Routes.OFFERS_INDEX,
        isOffersEnabled.value && ModuleCheck.hasAccess(Module.OFFERS_GRID),
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.GUESTS_DATABASE,
        $t('Guests database'),
        'user',
        Routes.GUESTS_INDEX_P1_P2,
        ModuleCheck.hasAccess(Module.GUESTS_GRID),
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.CONTACT_PERSONS,
        $t('Contact persons'),
        'user-tag',
        Routes.CONTACT_PERSONS_INDEX,
        ModuleCheck.hasAccess(Module.CONTACT_PERSONS_GRID),
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.FOREIGN_GUEST_REGISTER,
        $t('Foreign guest register'),
        'book',
        Routes.GUEST_BOOK_INDEX,
        ModuleCheck.hasAccess(Module.FOREIGN_GUEST_REGISTER),
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.GUEST_BOOK,
        $t('Guest book'),
        'book-user',
        Routes.GUEST_EVIDENCE_INDEX,
        ModuleCheck.hasAccess(Module.GUEST_BOOK),
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.COMPANIES,
        $t('Companies'),
        'building',
        isCompanyGridEnabled.value ? Routes.COMPANIES_INDEX : Routes.COMPANIES_INDEX_P1_P2,
        ModuleCheck.hasAccess(Module.COMPANIES_GRID),
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.MAILING,
        $t('Mailing'),
        'envelope',
        Routes.MAILING_INDEX_P1,
        !isCommunicationModuleEnabled.value && ModuleCheck.hasAccess(Module.MAILING),
      ),
      new PGlobalMenuWithSubMenu(
        PGlobalMenuItemKeys.MAILING,
        $t('Mailing'),
        'envelope',
        [
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.MAILING_AUTOMATED,
            CampaignCategory.AUTOMATED.name(),
            '',
            Routes.MAILING_INDEX_AUTOMATED,
            ModuleCheck.hasAccess(Module.MAILING),
          ),
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.MAILING_MARKETING,
            CampaignCategory.MARKETING.name(),
            '',
            Routes.MAILING_INDEX_MARKETING,
            ModuleCheck.hasAccess(Module.MAILING),
          ),
        ],
        isCommunicationModuleEnabled.value && ModuleCheck.hasAccess(Module.MAILING),
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.COMMUNICATION,
        $t('Communication'),
        'comments',
        Routes.COMMUNICATION_INDEX,
        ModuleCheck.hasAccess(Module.OTA_COMMUNICATION),
      ),
      new PGlobalMenuWithSubMenu(
        PGlobalMenuItemKeys.LOYALTY_PROGRAMME,
        $t('Loyalty programme'),
        'gift',
        [
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.LOYALTY_PROGRAMME_CARDS,
            $t('Cards'),
            '',
            Routes.BENEFIT_PROGRAM_CARDS_INDEX,
            ModuleCheck.hasAccess(Module.BENEFIT_PROGRAM_CARDS),
          ),
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.LOYALTY_PROGRAMME_POINTS,
            $t('Points on card'),
            '',
            Routes.BENEFIT_PROGRAM_TURNOVER_INDEX,
            ModuleCheck.hasAccess(Module.BENEFIT_PROGRAM_POINTS_ON_CARD),
          ),
        ],
      ),
    ]),
    new PGlobalMenuWithSubMenu(PGlobalMenuItemKeys.PAYMENTS_GROUP, $t('Payments'), 'coins', [
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.ACCOUNTING_DOCUMENTS,
        $t('Accounting documents'),
        'file-invoice',
        Routes.INVOICING_DOCUMENTS_INDEX_P1_P2,
        ModuleCheck.hasAccess(Module.PAYMENTS_INVOICING),
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.GIFT_VOUCHERS,
        $t('Gift vouchers'),
        'gift',
        Routes.INVOICING_GIFT_VOUCHERS_INDEX,
        isGiftVouchersEnabled.value && ModuleCheck.hasAccess(Module.PAYMENTS_GIFT_VOUCHERS),
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.FOLIOS,
        $t('Folio'),
        'file-user',
        Routes.FOLIO_INDEX_P1_P2,
        ModuleCheck.hasAccess(Module.PAYMENTS_FOLIO),
      ),
      new PGlobalMenuWithSubMenu(PGlobalMenuItemKeys.PAYMENTS, $t('Payments'), 'credit-card', [
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.PAYMENTS_CASHBOOK,
          $t('Cashbook'),
          '',
          Routes.INVOICING_PAYMENTS_CASH_BOOK_INDEX_P1_P2,
          ModuleCheck.hasAccess(Module.PAYMENTS_CASHBOOK_GRID),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.PAYMENTS_BANK_TRANSFERS,
          $t('Bank transfers'),
          '',
          Routes.INVOICING_PAYMENTS_BANK_TRANSFER_INDEX_P1_P2,
          ModuleCheck.hasAccess(Module.PAYMENTS_BANKTRANSFERS_GRID),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.PAYMENTS_PAYMENT_TERMINAL,
          $t('Payment terminal'),
          '',
          Routes.INVOICING_PAYMENTS_TERMINAL_PAYMENT_INDEX_P1_P2,
          ModuleCheck.hasAccess(Module.PAYMENTS_TERMINAL_GRID),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.PAYMENTS_ONLINE_PAYMENTS,
          $t('Online payments'),
          '',
          Routes.INVOICING_PAYMENTS_ONLINE_PAYMENT_INDEX_P1_P2,
          ModuleCheck.hasAccess(Module.PAYMENTS_ONLINE_GRID),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.PAYMENTS_COUPONS,
          $t('Coupons'),
          '',
          Routes.INVOICING_PAYMENTS_COUPON_INDEX_P1_P2,
          ModuleCheck.hasAccess(Module.PAYMENTS_COUPONS_GRID),
        ),
      ]),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.STOCK_TRANSACTIONS,
        $t('Stock transactions'),
        'warehouse',
        Routes.STOCK_TRANSACTIONS_INDEX,
        ModuleCheck.hasAccess(Module.PAYMENTS_STOCK_RETAIL_SALES),
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.CLOSURE,
        $t('Closure'),
        'money-bill-wave',
        Routes.CLOSURE_INDEX,
        ModuleCheck.hasAccess(Module.PAYMENTS_BALANCE),
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.EXCHANGE_OFFICE,
        $t('Exchange office'),
        'exchange-alt',
        Routes.EXCHANGES_INDEX,
        ModuleCheck.hasAccess(Module.PAYMENTS_EXCHANGE_OFFICE),
      ),
      new PGlobalMenuWithSubMenu(PGlobalMenuItemKeys.CODEBOOKS, $t('Codebooks'), 'sliders-h', [
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.ACCOUNT_ASSIGNMENT,
          $t('Account assignment'),
          '',
          Routes.ACCOUNTING_ASSIGNMENT_INDEX,
          ModuleCheck.hasAccess(Module.PAYMENTS_ACCOUNT_ASSIGNMENT),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.VAT_BREAKDOWN,
          $t('VAT breakdown'),
          '',
          Routes.VAT_CLASSIFICATION_INDEX,
          ModuleCheck.hasAccess(Module.PAYMENTS_VAT_BREAKDOWN),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.PKWIU,
          $t('PKWiU'),
          '',
          Routes.PKWIU_INDEX,
          ModuleCheck.hasAccess(Module.PAYMENTS_PL_SERVICE_CLASSIFICATION),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.GTU_CODES,
          $t('GTU'),
          '',
          Routes.GTU_CODES_INDEX,
          ModuleCheck.hasAccess(Module.PAYMENTS_PL_GTU_CODES),
        ),
      ]),
    ]),
    new PGlobalMenuWithSubMenu(PGlobalMenuItemKeys.REPORTS_GROUP, $t('Reports'), 'chart-pie', [
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.USER_ACTIVITY,
        $t('User activity'),
        'history',
        Routes.USER_ACTIVITY_INDEX,
        ModuleCheck.hasAccess(Module.REPORTS_USER_LOG),
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.STATISTICS_REPORTS,
        $t('Reports for Stat. Authority'),
        'clipboard-list-check',
        countryStrategy.statOfficeReportsRoute ?? Routes.STATISTICS_REPORTS_INDEX,
        countryStrategy.statOfficeReportsRoute !== null &&
          ModuleCheck.hasAccess(Module.REPORTS_FOR_STAT_AUTH),
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.DAILY_OVERVIEW,
        $t('Daily overview'),
        'list',
        Routes.DAILY_OVERVIEW_INDEX,
        ModuleCheck.hasAccess(Module.REPORTS_DAILY_OVERVIEW),
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.REPORTS_VISITOR_TAX,
        $t('Visitor tax'),
        'coins',
        Routes.VISITOR_TAX_REPORT_INDEX,
        ModuleCheck.hasAccess(Module.REPORTS_TAX_REPORT),
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.REVIEWS,
        $t('Reviews'),
        'comment',
        Routes.REVIEWS_INDEX,
        ModuleCheck.hasAccess(Module.REPORTS_GUEST_REVIEWS),
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.COMMISSIONS,
        $t('Commissions'),
        'sack',
        Routes.COMMISSIONS_INDEX,
        ModuleCheck.hasAccess(Module.REPORTS_BONUSES),
      ),
      new PGlobalMenuWithSubMenu(
        PGlobalMenuItemKeys.NTAK_GROUP,
        $t('Ntak'),
        'suitcase-rolling',
        [
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.NTAK_REPORTS,
            $t('Reports'),
            '',
            Routes.NTAK_INDEX,
            ModuleCheck.hasAccess(Module.REPORTS_NTAK_REPORT),
          ),
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.NTAK_REVENUES,
            $t('Other monthly revenues'),
            '',
            Routes.NTAK_REVENUES_INDEX,
            ModuleCheck.hasAccess(Module.REPORTS_NTAK_REPORT),
          ),
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.NTAK_EXPENSES,
            $t('Expenses'),
            '',
            Routes.NTAK_EXPENSES_INDEX,
            ModuleCheck.hasAccess(Module.REPORTS_NTAK_REPORT),
          ),
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.NTAK_CONNECTION,
            $t('Connection'),
            '',
            Routes.NTAK_CONNECTION_INDEX,
            ModuleCheck.hasAccess(Module.REPORTS_NTAK_REPORT),
          ),
        ],
        !countryStrategy.isMenuPointDisabled(PGlobalMenuItemKeys.NTAK_GROUP),
      ),
      new PGlobalMenuWithSubMenu(
        PGlobalMenuItemKeys.VISA_GROUP,
        $t('Viza'),
        'passport',
        [
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.VISA_STATISTICS,
            $t('Statistics'),
            '',
            Routes.VISA_STATISTICS_INDEX,
            ModuleCheck.hasAccess(Module.REPORTS_NTAK_REPORT),
          ),
        ],
        !countryStrategy.isMenuPointDisabled(PGlobalMenuItemKeys.VISA_GROUP),
      ),
      new PGlobalMenuWithSubMenu(
        PGlobalMenuItemKeys.MANAGER_REPORTS_GROUP,
        $t('Manager reports'),
        'tasks-alt',
        [
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.MANAGER_REPORTS_DASHBOARD,
            $t('Dashboard'),
            '',
            Routes.DASHBOARD_INDEX,
            ModuleCheck.hasAccess(Module.REPORTS_DASHBOARD),
          ),
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.MANAGER_REPORTS_ALFRED,
            $t('Alfred Dashboard'),
            '',
            Routes.ALFRED_DASHBOARD_INDEX,
            ModuleCheck.hasAccess(Module.REPORTS_ALFRED_DASHBOARD),
          ),
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.MANAGER_REPORTS_OCCUPANCY,
            $t('Occupancy'),
            '',
            Routes.REPORTS_OCCUPANCY_INDEX,
            ModuleCheck.hasAccess(Module.REPORTS_OCCUPANCY),
          ),
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.MANAGER_REPORTS_RESERVATION,
            $t('Reservation'),
            '',
            Routes.REPORTS_RESERVATION_INDEX,
            ModuleCheck.hasAccess(Module.REPORTS_RESERVATION),
          ),
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.MANAGER_REPORTS_PICK_UP_REPORT,
            $t('Pick-up'),
            '',
            Routes.REPORTS_PICKUP_INDEX,
            ModuleCheck.hasAccess(Module.REPORTS_PICKUP),
          ),
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.MANAGER_REPORTS_FINANCE,
            $t('Finance'),
            '',
            Routes.REPORTS_ACCOUNTS_INDEX,
            ModuleCheck.hasAccess(Module.REPORTS_FINANCE),
          ),
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.MANAGER_REPORTS_OVERVIEW,
            $t('Overview'),
            '',
            Routes.REPORTS_OVERVIEW_INDEX,
            ModuleCheck.hasAccess(Module.REPORTS_OVERVIEW),
          ),
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.MANAGER_REPORTS_HOTEL_GROUP_OVERVIEW,
            $t('Hotelgroup overview'),
            '',
            Routes.REPORTS_HOTELGROUP_OVERVIEW_INDEX,
            ModuleCheck.hasAccess(Module.REPORTS_OVERVIEW),
          ),
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.MANAGER_REPORTS_NTAK_KPI,
            $t('KPI'),
            '',
            Routes.REPORTS_NTAK_KPI_INDEX,
            ModuleCheck.hasAccess(Module.REPORTS_NTAK_REPORT) &&
              !countryStrategy.isMenuPointDisabled(PGlobalMenuItemKeys.MANAGER_REPORTS_NTAK_KPI),
          ),
        ],
      ),
    ]),
    new PGlobalMenuWithSubMenu(
      PGlobalMenuItemKeys.PRICES_GROUP,
      $t('Pricelist'),
      'money-bill-alt',
      [
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.PRICE_LIST,
          $t('Pricelist'),
          'bed',
          Routes.PRICE_LIST_INDEX,
          ModuleCheck.hasAccess(Module.PRICES),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.MEALS_LIST,
          $t('Meals'),
          'utensils',
          Routes.MEALS_LIST_INDEX,
          ModuleCheck.hasAccess(Module.PRICES_MEALS),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.REVENUE_MANAGEMENT,
          $t('Revenue management'),
          'percent',
          Routes.REVENUE_MANAGEMENT_INDEX,
          ModuleCheck.hasAccess(Module.PRICES_REVENUE_MANAGEMENT),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.PRICES_EXCHANGE_RATES,
          $t('Currencies & exchange rates'),
          'exchange',
          isExchangeRatesEnabled.value
            ? Routes.EXCHANGE_RATES_INDEX
            : Routes.EXCHANGE_RATES_INDEX_P1,
          ModuleCheck.hasAccess(Module.PRICES_CURRENCIES_EXCHANGE_RATES),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.PRICES_VISITOR_TAX,
          $t('Taxes & fees'),
          'coins',
          isTaxSettingsEnabled.value ? Routes.VISITOR_TAX_INDEX : Routes.VISITOR_TAX_INDEX_P1,
          ModuleCheck.hasAccess(Module.PRICES_TAX_SETTINGS),
        ),
        new PGlobalMenuWithSubMenu(
          PGlobalMenuItemKeys.DISCOUNTS_GROUP,
          $t('Discounts / surcharges'),
          'passport',
          [
            new PGlobalMenuItem(
              PGlobalMenuItemKeys.DISCOUNT_LONG_TERM,
              $t('Long term stay'),
              '',
              Routes.DISCOUNT_LONG_TERM_INDEX,
              ModuleCheck.hasAccess(Module.PRICES_LONG_TERM_STAY),
            ),
            new PGlobalMenuItem(
              PGlobalMenuItemKeys.DISCOUNT_FIRST_MINUTE,
              $t('First minute'),
              '',
              Routes.DISCOUNT_FIRST_MINUTE_INDEX,
              ModuleCheck.hasAccess(Module.PRICES_FIRST_MINUTE),
            ),
            new PGlobalMenuItem(
              PGlobalMenuItemKeys.DISCOUNT_LAST_MINUTE,
              $t('Last minute'),
              '',
              Routes.DISCOUNT_LAST_MINUTE_INDEX,
              ModuleCheck.hasAccess(Module.PRICES_LAST_MINUTE),
            ),
            new PGlobalMenuItem(
              PGlobalMenuItemKeys.DISCOUNT_SPECIAL_PRICE,
              $t('Special price'),
              '',
              Routes.DISCOUNT_SPECIAL_PRICE_INDEX,
              ModuleCheck.hasAccess(Module.PRICES_ACTION_PRICE),
            ),
            new PGlobalMenuItem(
              PGlobalMenuItemKeys.DISCOUNT_COUPONS,
              $t('Discount coupons'),
              '',
              Routes.DISCOUNT_COUPONS_INDEX,
              ModuleCheck.hasAccess(Module.PRICES_DISCOUNT_COUPONS),
            ),
          ],
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.SERVICES,
          $t('Services'),
          'hand-holding-heart',
          Routes.SERVICES_INDEX,
          ModuleCheck.hasAccess(Module.PRICES_SERVICES),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.STOCK_ITEMS,
          $t('Stock items'),
          'boxes',
          Routes.STOCK_INDEX,
          ModuleCheck.hasAccess(Module.PAYMENTS_STOCK_ITEMS),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.STAY_PACKAGES,
          $t('Stay packages'),
          'gift',
          Routes.STAY_PACKAGES_INDEX,
          ModuleCheck.hasAccess(Module.PRICES_STAY_PACKAGES),
        ),
      ],
    ),
    new PGlobalMenuWithSubMenu(PGlobalMenuItemKeys.SETTINGS_GROUP, $t('Settings'), 'cog', [
      new PGlobalMenuWithSubMenu(PGlobalMenuItemKeys.SETTINGS_HOTEL_GROUP, $t('Hotel'), 'hotel', [
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.HOTEL_GENERAL_SETTINGS,
          $t('General'),
          '',
          Routes.HOTEL_GENERAL_INDEX,
          ModuleCheck.hasAccess(Module.SETTINGS_HOTEL_GENERAL),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.HOTEL_AMENITIES,
          $t('Amenities'),
          '',
          Routes.HOTEL_AMENITIES_INDEX,
          ModuleCheck.hasAccess(Module.SETTINGS_HOTEL_AMENITIES),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.HOTEL_PHOTO_GALLERIES,
          $t('Photo galleries'),
          '',
          Routes.HOTEL_PHOTOGALLERIES_INDEX,
          ModuleCheck.hasAccess(Module.SETTINGS_HOTEL_PHOTOGALLERIES),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.HOTEL_GUEST_CATEGORIES,
          $t('Guest categories'),
          '',
          Routes.HOTEL_GUEST_CATEGORIES_INDEX,
          ModuleCheck.hasAccess(Module.SETTINGS_HOTEL_GUEST_CATEGORIES),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.HOTEL_ROOMS,
          $t('Rooms'),
          '',
          Routes.HOTEL_ROOMS_INDEX,
          ModuleCheck.hasAccess(Module.SETTINGS_HOTEL_ROOMS),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.HOTEL_CONNECTORS,
          $t('Connectors'),
          '',
          Routes.HOTEL_CONNECTORS_INDEX,
          ModuleCheck.hasAccess(Module.SETTINGS_HOTEL_CONNECTORS),
        ),
      ]),
      new PGlobalMenuWithSubMenu(
        PGlobalMenuItemKeys.ONLINE_RESERVATIONS_GROUP,
        $t('Online reservations'),
        'globe-americas',
        [
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.BOOKING_SETTINGS,
            $t('Reservation +'),
            '',
            Routes.SETTINGS_ONLINE_RESERVATIONS_BOOKING_FORM_CONFIGURATION,
            ModuleCheck.hasAccess(Module.ONLINE_RESERVATION_BOOKING),
          ),
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.PAYMENTS_SETTINGS,
            $t('Payments'),
            '',
            Routes.ONLINE_RESERVATIONS_PAYMENTS_INDEX,
            ModuleCheck.hasAccess(Module.SETTINGS_ONLINE_RESERVATION_PAYMENTS),
          ),
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.TERMS_AND_CONDITIONS,
            $t('Terms and conditions'),
            '',
            Routes.ONLINE_RESERVATIONS_TERMS_AND_CONDITIONS_INDEX,
            ModuleCheck.hasAccess(Module.SETTINGS_ONLINE_RESERVATION_TERMS_AND_CONDITIONS),
          ),
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.PRIVACY_POLICY,
            $t('Processing of personal data'),
            '',
            Routes.ONLINE_RESERVATIONS_PRIVACY_POLICY_INDEX,
            ModuleCheck.hasAccess(Module.ONLINE_RESERVATION_BOOKING),
          ),
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.MY_CONNECTIONS,
            $t('My connections'),
            '',
            Routes.SETTINGS_CHANNEL_MANAGER_MY_CONNECTIONS_INDEX,
          ),
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.SERVICE_MAPPING,
            $t('Service mapping'),
            '',
            Routes.SETTINGS_CHANNEL_MANAGER_SERVICE_MAPPING_INDEX,
            isServiceMappingEnabled.value,
          ),
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.MARKETPLACE,
            $t('Marketplace'),
            '',
            Routes.SETTINGS_CHANNEL_MANAGER_MARKETPLACE_INDEX,
          ),
        ],
      ),
      new PGlobalMenuWithSubMenu(PGlobalMenuItemKeys.WEB_GROUP, $t('Web'), 'browser', [
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.WEB_GENERAL,
          $t('General'),
          '',
          Routes.WEB_GENERAL_INDEX,
          ModuleCheck.hasAccess(Module.SETTINGS_WEB_GENERAL) && !propertyModuleStore.isWordPress,
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.WEB_SECTIONS,
          $t('Web sections'),
          '',
          Routes.WEB_SECTIONS_INDEX,
          ModuleCheck.hasAccess(Module.SETTINGS_WEB_WEBSECTIONS) &&
            !propertyModuleStore.isWordPress,
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.WEB_NEWS,
          $t('News'),
          '',
          Routes.WEB_NEWS_INDEX,
          ModuleCheck.hasAccess(Module.SETTINGS_WEB_NEWS) && !propertyModuleStore.isWordPress,
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.UPDATE_WP_DATA,
          $t('Update data'),
          '',
          Routes.WEB_UPDATE_WP_DATA,
          ModuleCheck.hasAccess(Module.SETTINGS_WEB_UPDATE_WP_DATA) &&
            propertyModuleStore.isWordPress,
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.LOGIN_WP_ADMIN,
          $t('Log in'),
          '',
          Routes.WEB_LOGIN_WP_ADMIN,
          ModuleCheck.hasAccess(Module.SETTINGS_WEB_LOGIN_WP_ADMIN) &&
            propertyModuleStore.isWordPress,
        ),
      ]),
      new PGlobalMenuWithSubMenu(
        PGlobalMenuItemKeys.APP_GROUP,
        $t('Applications'),
        'mobile-notch',
        [
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.ALFRED_APP_SETTINGS,
            $t('Alfred'),
            'concierge-bell',
            Routes.SETTINGS_ALFRED_GENERAL_INDEX,
            ModuleCheck.hasAccess(Module.SETTINGS_ALFRED_APP),
          ),
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.SELF_SERVICE_BAR_APP_SETTINGS,
            $t('Self service bar'),
            'qrcode',
            Routes.SELF_SERVICE_BAR_INDEX,
            ModuleCheck.hasAccess(Module.SETTINGS_SELF_SERVICE_BAR),
          ),
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.POS_APP_SETTINGS,
            $t('POS'),
            'cash-register',
            Routes.POS_INDEX,
            ModuleCheck.hasAccess(Module.SETTINGS_POS),
          ),
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.CONNECTED_ROOMS_APP_SETTINGS,
            $t('Connected rooms'),
            'link',
            Routes.SETTINGS_CONNECTED_ROOMS_CONNECTIONS_INDEX,
            isConnectedRoomsEnabled.value && ModuleCheck.hasAccess(Module.SETTINGS_HOTEL_ROOMS),
          ),
        ],
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.LOCK_SYSTEMS_SETTINGS,
        $t('Lock systems'),
        'key',
        Routes.SETTINGS_LOCK_SYSTEMS_SETTINGS,
        ModuleCheck.hasAccess(Module.SETTINGS_LOCK_SYSTEMS),
      ),
      new PGlobalMenuWithSubMenu(PGlobalMenuItemKeys.USERS_GROUP, $t('Users'), 'users', [
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.ROLES_SETTINGS,
          $t('Role'),
          '',
          Routes.ROLES_INDEX,
          ModuleCheck.hasAccess(Module.SETTINGS_USERS_ROLES),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.USERS_SETTINGS,
          $t('Users'),
          '',
          Routes.USERS_INDEX,
          ModuleCheck.hasAccess(Module.SETTINGS_USERS_USER_LIST),
        ),
      ]),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.DESCRIPTIONS,
        $t('Descriptions'),
        'comment-medical',
        Routes.DESCRIPTIONS_INDEX,
        ModuleCheck.hasAccess(Module.SETTINGS_TRANSLATIONS),
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.MARKET_CODES,
        $t('Market codes'),
        'tags',
        Routes.MARKET_CODES_INDEX,
        ModuleCheck.hasAccess(Module.SETTINGS_MARKET_CODES),
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.SIGNIFICANT_DATES,
        $t('Significant dates'),
        'birthday-cake',
        Routes.SIGNIFICANT_DATES_INDEX,
        ModuleCheck.hasAccess(Module.SETTINGS_IMPORTANT_DAYS),
      ),
      new PGlobalMenuWithSubMenu(PGlobalMenuItemKeys.SYSTEM_SETTINGS_GROUP, $t('System'), 'cog', [
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.SYSTEM_SETTINGS_RESERVATION,
          $t('Reservation'),
          '',
          hasReservationSettingsEnabled.value
            ? Routes.SETTINGS_RESERVATIONS_GENERAL
            : Routes.SYSTEM_RESERVATION_INDEX,
          ModuleCheck.hasAccess(Module.SETTINGS_SYSTEM) &&
            PrivilegeCheck.hasPrivilege(Privilege.CANVAS_SETTING_EDIT),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.SYSTEM_SETTINGS_VOUCHER,
          $t('Voucher'),
          '',
          hasReservationSettingsEnabled.value
            ? Routes.SETTINGS_RESERVATIONS_VOUCHER
            : Routes.SYSTEM_VOUCHER_INDEX,
          ModuleCheck.hasAccess(Module.SETTINGS_SYSTEM) &&
            PrivilegeCheck.hasPrivilege(Privilege.PRICING_SETUP),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.SYSTEM_SETTINGS_FOLIO,
          $t('Folio'),
          '',
          hasReservationSettingsEnabled.value
            ? Routes.SETTINGS_RESERVATIONS_FOLIO
            : Routes.SYSTEM_HOTEL_ACCOUNT_INDEX,
          ModuleCheck.hasAccess(Module.SETTINGS_SYSTEM) &&
            PrivilegeCheck.hasPrivilege(Privilege.CANVAS_SETTING_EDIT),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.SYSTEM_SETTINGS_REPORTS,
          $t('Reports'),
          '',
          Routes.SYSTEM_REPORTS_INDEX,
          ModuleCheck.hasAccess(Module.SETTINGS_SYSTEM),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.SYSTEM_SETTINGS_EMAIL,
          $t('Email'),
          '',
          Routes.SETTINGS_HOTEL_EMAIL,
          ModuleCheck.hasAccess(Module.SETTINGS_EMAIL_SETTINGS),
        ),
      ]),
      new PGlobalMenuWithSubMenu(
        PGlobalMenuItemKeys.NOTIFICATION_SETTINGS,
        $t('Notifications'),
        'bell',
        [
          new PGlobalMenuItem(
            PGlobalMenuItemKeys.NOTIFICATION_SETTINGS_RESERVATION,
            $t('Reservation'),
            'calendar-lines',
            Routes.SETTINGS_NOTIFICATIONS_RESERVATION,
            true,
          ),
        ],
      ),
    ]),
    new PGlobalMenuWithSubMenu(
      PGlobalMenuItemKeys.HOUSEKEEPING_GROUP,
      $t('Housekeeping'),
      'home-lg',
      [
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.CLEANING,
          $t('Cleaning'),
          'vacuum',
          Routes.HOUSEKEEPING_INDEX,
          ModuleCheck.hasAccess(Module.HOUSEKEEPING_CLEANING_GRID),
        ),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.DEFECTS,
          $t('Defects'),
          'tools',
          Routes.DEFECTS_INDEX,
          ModuleCheck.hasAccess(Module.HOUSEKEEPING_DEFECT_GRID),
        ),
        new PGlobalMenuItem(PGlobalMenuItemKeys.TASKS, $t('Tasks'), 'tasks', Routes.TASKS_INDEX),
        new PGlobalMenuItem(
          PGlobalMenuItemKeys.TASK_TEMPLATES,
          $t('Automatic tasks'),
          'clipboard-clock',
          Routes.TASK_TEMPLATES_INDEX,
        ),
      ],
    ),
    new PGlobalMenuWithSubMenu(PGlobalMenuItemKeys.HELP_GROUP, $t('Help'), 'question-circle', [
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.HELP_INVOICES,
        $t('Invoices'),
        'file-invoice',
        Routes.HELP_INVOICES_INDEX,
        ModuleCheck.hasAccess(Module.HELP_INVOICES),
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.HELP_MANUAL,
        $t('Manuals'),
        'hands',
        Routes.HELP_MANUAL_INDEX,
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.HELP_USER_TIPS,
        $t('User tips'),
        'lightbulb-on',
        Routes.HELP_USER_TIPS_INDEX,
        ModuleCheck.hasAccess(Module.HELP_USER_TIPS),
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.HELP_FEEDBACK,
        $t('Feedback'),
        'comment-edit',
        Routes.HELP_FEEDBACK_INDEX,
        ModuleCheck.hasAccess(Module.HELP),
      ),
      new PGlobalMenuItem(
        PGlobalMenuItemKeys.HELP_DEMO,
        $t('Open demo version'),
        'display',
        Routes.HELP_DEMO_INDEX,
        ModuleCheck.hasAccess(Module.HELP),
      ),
    ]),
  )

  return modules
})
